<script>
import {
  provide, watch,
} from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies';
import { mapGetters, mapMutations, mapState } from 'vuex';
import SwordAlertBanner from '@ui-kit/components/alert/AlertBanner.vue';
import { applicationStoreTypes } from '@/store/types';

// PageInfo Ctrl
import { usePageInfo } from '@/plugins/page-info';
import { COOKIES_KEYS } from '@/scripts/constants';

// Components
import FeedbackScreen from '@/components/layout/FeedbackScreen.vue';
import SwordHeader from '@/components/layout/SWORDHeader.vue';
import SwordFooter from '@/components/layout/SWORDFooter.vue';
import InterstitialScreen from '@/components/InterstitialScreen.vue';
import PageTitle from './PageTitle.vue';

export default {
  name: 'Layout',
  components: {
    PageTitle,
    SwordFooter,
    InterstitialScreen,
    SwordHeader,
    FeedbackScreen,
    SwordAlertBanner,
  },
  props: {
    showMenu: {
      type: Boolean,
      default: true,
    },
    showBrandLogo: {
      type: Boolean,
      default: true,
    },
    footerLinks: {
      type: Object,
      default: undefined,
    },
    footerMinimal: Boolean,
  },
  setup() {
    const { pageInfo, resetPageInfo } = usePageInfo();

    provide('pageInfo', pageInfo);

    watch(() => pageInfo.isLoading, val => {
      if (val) window.scrollTo(0, 0);
    });

    const cookies = useCookies([COOKIES_KEYS.REFERRAL_TOKEN]);

    return {
      pageInfo,
      resetPageInfo,
      cookies,
    };
  },
  data() {
    return {
      decodedReferralToken: this.decodeBase64Token(this.cookies.get(COOKIES_KEYS.REFERRAL_TOKEN)),
      isTransitionEnd: undefined,
    };
  },
  computed: {
    ...mapGetters({
      clientToggles: 'client/getClientToggles',
      isReferralBannedClosed: applicationStoreTypes.getters.IS_REFERRAL_BANNER_CLOSED,
    }),
    ...mapState({
      interstitialData: state => state.application.interstitialData,
    }),
    isShowReferralBanner() {
      return this.decodedReferralToken && !this.isReferralBannedClosed;
    },
    alertBannerMessage() {
      const inviterName = this.decodedReferralToken?.split('|')[2] ?? '';
      return this.$t('onboarding_referral_alertmessage', {
        name: inviterName,
      });
    },
    isShowPage() {
      if (this.isTransitionEnd === undefined) {
        return !this.interstitialData && !this.pageInfo.isLoading;
      }

      return this.isTransitionEnd && !this.interstitialData && !this.pageInfo.isLoading;
    },
    showPageTitle() {
      return this.pageInfo.title || this.pageInfo.subTitle || this.pageInfo.currentPageName;
    },
    withAnimation() {
      return this.pageInfo.withAnimation;
    },
  },
  watch: {
    isShowPage(value) {
      if (value) {
        this.$nextTick(() => {
          this.isTransitionEnd = undefined;
        });
      }
    },
  },
  mounted() {
    this.cookies.addChangeListener(this.handleCookieChange);
  },
  beforeDestroy() {
    this.cookies.removeChangeListener(this.handleCookieChange);
  },
  methods: {
    ...mapMutations({
      setIsReferralBannedClosed: applicationStoreTypes.mutations.SET_IS_REFERRAL_BANNER_CLOSED,
      setInterstitialData: applicationStoreTypes.mutations.SET_INTERSTITIAL_DATA,
    }),
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      });
    },
    handleCookieChange(cookieChanged) {
      if (cookieChanged.name === COOKIES_KEYS.REFERRAL_TOKEN) {
        this.decodedReferralToken = this.decodeBase64Token(cookieChanged.value);
      }
    },
    decodeBase64Token(encodedToken) {
      let decodedToken = null;

      if (encodedToken) {
        try {
          decodedToken = Buffer.from(encodedToken, 'base64').toString('utf-8');
        } catch (error) {
          console.error('Failed to decode referral token: ', error.message);
        }
      }

      return decodedToken;
    },
    handleTransitionStatus(value) {
      this.isTransitionEnd = value;
    },
    handleNextOnInterstitialScreen() {
      this.setInterstitialData(null);
      this.scrollToTop();
    },
  },
};
</script>

<template>
  <div class="layout">
    <sword-alert-banner
      v-if="isShowReferralBanner"
      :text="alertBannerMessage"
      color="blue"
      container-width="600px"
      @update:show="() => setIsReferralBannedClosed(true)"
    />

    <sword-header
      :show-menu="showMenu"
      :show-brand-logo="showBrandLogo"
    />

    <main class="layout__main flex-col-full">
      <div v-show="isShowPage" class="flex-col-full">
        <slot>
          <portal-target name="step-progress-bar" />
        </slot>
        <page-title v-if="showPageTitle" class="layout-page-title" show-back :animated="withAnimation" />
        <router-view />
      </div>

      <transition
        name="fade"
        mode="out-in"
        @enter="handleTransitionStatus(false)"
        @after-leave="handleTransitionStatus(true)"
      >
        <interstitial-screen
          v-if="interstitialData"
          :title="interstitialData.title"
          :subtitle="interstitialData.subtitle"
          :image-url="interstitialData.image_url"
          :disclaimer="interstitialData.description"
          :loading="pageInfo.isLoading"
          @next="handleNextOnInterstitialScreen"
        />
      </transition>

      <feedback-screen
        v-if="!interstitialData && pageInfo.isLoading"
        type="loading"
        :title="$t(pageInfo.loadTitle || 'onboarding_message_just_a_moment')"
        :text="$t(pageInfo.loadSubTitle || 'onboarding2_clinical_loading_message')"
      />
    </main>
    <sword-footer class="layout__footer" :links="footerLinks" :minimal="footerMinimal" />
  </div>
</template>

<style lang="scss" scoped>
$header-height: 56px;

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s linear;
}

.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__main {
    position: relative;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 2.5rem;
    width: 100%;
    min-height: calc(100vh - $header-height);
    max-width: 688px;
    margin: auto;

    @media screen and (min-width: $max-smartphone-size) {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
  }

  .layout-page-title {
    margin-top: 2rem;

    :deep(.page__subtitle) a {
      color: $color-secondary-500;
    }
  }
}

.flex-col-full {
  display: flex;
  flex-direction: column;

  @media screen and (min-width: $max-smartphone-size) {
    flex: 1;
  }
}
</style>
